import React, { useState, useRef, useEffect, useMemo } from 'react';
import _ from 'lodash';

const ASSETS_URL = ''

const Connections = ({ items = [], onComplete, isCompleted: externalIsCompleted, cardIndex }) => {
  const storageKey = useMemo(() => `connections_state_${cardIndex}`, [cardIndex]);
  const shufflestorageKey = useMemo(() => `shuffled_items_${cardIndex}`, [cardIndex]);
  const [connections, setConnections] = useState(new Set());
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  const containerRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [wrongConnections, setWrongConnections] = useState(new Set());
  const [highlightedCorrect, setHighlightedCorrect] = useState(new Set());

  // Separate restoration effect
  useEffect(() => {
    if (!isRestored) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsedConnections = new Set(JSON.parse(stored).connections);
          requestAnimationFrame(() => {
            setConnections(parsedConnections);
            setIsRestored(true);
          });
        } else {
          setIsRestored(true);
        }
      } catch (e) {
        console.log('Error reading stored connections:', e);
        setIsRestored(true);
      }
    }
  }, [storageKey, isRestored]);

  // Handle external completion
  useEffect(() => {
    if (externalIsCompleted && !isCorrect) {
      const isPageRefresh = !sessionStorage.getItem('app_loaded');
      if (!isPageRefresh) {
        const stored = localStorage.getItem(storageKey);
        const shuffleStored = localStorage.getItem(shufflestorageKey);
        if (stored) {
          const parsedData = JSON.parse(stored);
          setConnections(new Set(parsedData.connections));
        }
        if (shuffleStored) {
          const parsedShuffleData = JSON.parse(shuffleStored);
          setShuffledFromItems(parsedShuffleData.shuffledFromItems);
          setShuffledToItems(parsedShuffleData.shuffledToItems);
        }
      }
      setIsCorrect(true);
      setShowSuccess(true);
    }
  }, [externalIsCompleted, isCorrect, storageKey]);

  // Create shuffled arrays for both columns
  const [shuffledFromItems, setShuffledFromItems] = useState(() => {
    const shuffleStored = localStorage.getItem(shufflestorageKey);
    if (shuffleStored && isCorrect) {
      const parsedData = JSON.parse(shuffleStored);
      return parsedData.shuffledFromItems;
    }
    
    const fromItems = items.map(item => ({
      ...item,
      key: `from-${item.fromText}-${item.pairId}`
    }));
    
    return _.shuffle(fromItems);
  });

  const [shuffledToItems, setShuffledToItems] = useState(() => {
    const shuffleStored = localStorage.getItem(shufflestorageKey);
    if (shuffleStored && isCorrect) {
      const parsedData = JSON.parse(shuffleStored);
      return parsedData.shuffledToItems;
    }
    
    const toItems = items.map(item => ({
      ...item,
      key: `to-${item.toText}-${item.pairId}`
    }));
    
    return _.shuffle(toItems);
  });

  const validConnectionsMap = useMemo(() => {
    const map = new Map();
    items.forEach(({ fromText, toText }) => {
      if (!map.has(fromText)) {
        map.set(fromText, new Set());
      }
      map.get(fromText).add(toText);
    });
    return map;
  }, [items]);

  useEffect(() => {
    const hasStoredShuffle = localStorage.getItem(shufflestorageKey);
    if (!hasStoredShuffle) {  // Only save if no shuffle exists
      try {
        const dataToStore = {
          shuffledFromItems,
          shuffledToItems,
          timestamp: Date.now()
        };
        localStorage.setItem(shufflestorageKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.error('Error saving shuffled items:', error);
      }
    }
  }, []); 

  useEffect(() => {
    if (!selectedFrom) return;

    const handleMouseMove = (e) => {
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [selectedFrom]);

  // Position calculation effect
  useEffect(() => {
    const updatePositions = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPositions = {};

      container.querySelectorAll('[data-from]').forEach(el => {
        const rect = el.getBoundingClientRect();
        const fromKey = el.dataset.fromKey;
        newPositions[`from-${fromKey}`] = {
          x: rect.right - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2
        };
      });

      container.querySelectorAll('[data-to]').forEach(el => {
        const rect = el.getBoundingClientRect();
        const toText = el.dataset.to;
        newPositions[`to-${toText}`] = {
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2
        };
      });

      setPositions(newPositions);
    };

    // Initial position calculation
    updatePositions();

    // Update positions whenever connections change
    const timer = setTimeout(updatePositions, 50);

    // Handle window resize
    const handleResize = _.debounce(updatePositions, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [connections, items]);

  const validateConnections = (connections) => {
    const requiredFromTexts = new Set(items.map(item => item.fromText));
    const connectionsByFrom = new Map();
    [...connections].forEach(conn => {
      const [fromText, , toText] = conn.split('-');
      if (!connectionsByFrom.has(fromText)) {
        connectionsByFrom.set(fromText, []);
      }
      connectionsByFrom.get(fromText).push(toText);
    });

    for (const fromText of requiredFromTexts) {
      const currentConnections = connectionsByFrom.get(fromText) || [];
      const validConnections = validConnectionsMap.get(fromText);
      
      const count = items.filter(item => item.fromText === fromText).length;
      if (currentConnections.length !== count) {
        return false;
      }
      
      if (!currentConnections.every(toText => validConnections.has(toText))) {
        return false;
      }
    }

    return true;
  };

  const handleConnection = (fromItem, type) => {
    if (isCorrect) return;
    
    if (type === 'from') {
      // Clear previous feedback when selecting new source
      setWrongConnections(new Set());
      setHighlightedCorrect(new Set());
      
      // Also remove any wrong connections from the connections set
      const newConnections = new Set();
      [...connections].forEach(conn => {
        const [from, connPairId, to] = conn.split('-');
        // Only keep connections that are valid
        if (validConnectionsMap.get(from)?.has(to)) {
          newConnections.add(conn);
        }
      });
      setConnections(newConnections);
      
      setSelectedFrom(fromItem);
    } else if (selectedFrom) {
      const { fromText, pairId } = selectedFrom;
      const connection = `${fromText}-${pairId}-${fromItem}`;
      
      // Check if this is a valid connection
      const isValidConnection = validConnectionsMap.get(fromText)?.has(fromItem);
      
      const newConnections = new Set(connections);
      const newWrongConnections = new Set(wrongConnections);
      const newHighlightedCorrect = new Set(highlightedCorrect);
      
      if (!newConnections.has(connection)) {
        // Remove any existing connection from this source
        [...newConnections].forEach(conn => {
          const [from, connPairId] = conn.split('-');
          if (from === fromText && connPairId === pairId.toString()) {
            newConnections.delete(conn);
          }
        });
        
        if (isValidConnection) {
          // Correct connection - add it normally
          newConnections.add(connection);
          // Clear any wrong connection feedback for this target
          newWrongConnections.delete(fromItem);
        } else {
          // Wrong connection - show feedback
          newConnections.add(connection);
          newWrongConnections.add(fromItem);
          
          // Highlight the correct target(s) in green
          const correctTargets = validConnectionsMap.get(fromText);
          if (correctTargets) {
            correctTargets.forEach(correctTarget => {
              newHighlightedCorrect.add(correctTarget);
            });
          }
        }
      }
      
      setConnections(newConnections);
      setWrongConnections(newWrongConnections);
      setHighlightedCorrect(newHighlightedCorrect);
      setSelectedFrom(null);

      // Only check for completion if no wrong connections
      if (newWrongConnections.size === 0) {
        const isValid = validateConnections(newConnections);

        if (isValid) {
          const isPageRefresh = !sessionStorage.getItem('app_loaded');
          if (!isPageRefresh) {
            localStorage.setItem(storageKey, JSON.stringify({
              connections: [...newConnections],
              timestamp: Date.now()
            }));
          }
          
          requestAnimationFrame(() => {
            setIsCorrect(true);
            
            setTimeout(() => {
              setShowSuccess(true);
              onComplete?.();
              
              try {
                const audio = new Audio('/well-done.mp3');
                audio.play().catch(() => {
                  console.log('Audio not available');
                });
              } catch (error) {
                console.log('Audio playback error');
              }
            }, 50);
          });
        }
      }
    }
  };

  const isItemConnected = (item, pairId, type) => {
    return [...connections].some(connection => {
      const [from, connPairId, to] = connection.split('-');
      if (type === 'from') {
        return from === item && connPairId === pairId.toString();
      }
      return to === item;
    });
  };

  if (!Array.isArray(items)) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        Invalid connection items provided
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="relative grid grid-cols-2 gap-8 p-4 bg-slate-50 rounded-lg"
      style={{ minHeight: '200px' }}
    >
      {isRestored && (
        <svg 
          className="absolute inset-0 pointer-events-none" 
          style={{ width: '100%', height: '100%' }}
        >
          {[...connections].map(connection => {
            const [from, pairId, to] = connection.split('-');
            const fromPos = positions[`from-${from}-${pairId}`];
            const toPos = positions[`to-${to}`];
            
            if (!fromPos || !toPos) return null;

            const isWrongConnection = wrongConnections.has(to);
            const strokeColor = isCorrect ? "#22c55e" : isWrongConnection ? "#ef4444" : "#3b82f6";

            return (
              <g key={connection}>
                <line
                  x1={fromPos.x}
                  y1={fromPos.y}
                  x2={toPos.x}
                  y2={toPos.y}
                  stroke={strokeColor}
                  strokeWidth="2"
                />
                <circle
                  cx={fromPos.x}
                  cy={fromPos.y}
                  r="4"
                  fill={strokeColor}
                />
                <circle
                  cx={toPos.x}
                  cy={toPos.y}
                  r="4"
                  fill={strokeColor}
                />
              </g>
            );
          })}
          
          {selectedFrom && positions[`from-${selectedFrom.fromText}-${selectedFrom.pairId}`] && (
            <line
              x1={positions[`from-${selectedFrom.fromText}-${selectedFrom.pairId}`].x}
              y1={positions[`from-${selectedFrom.fromText}-${selectedFrom.pairId}`].y}
              x2={mousePosition.x}
              y2={mousePosition.y}
              stroke="#3b82f6"
              strokeWidth="2"
              strokeDasharray="4"
              className="animate-pulse"
            />
          )}
        </svg>
      )}

      {/* Left Column - FROM items with word-list styling */}
{/* Left Column - FROM items with word-list styling */}
<div className="space-y-3">
  {shuffledFromItems.map(item => {
    const isSelected = selectedFrom?.fromText === item.fromText && selectedFrom?.pairId === item.pairId;
    const isConnected = isItemConnected(item.fromText, item.pairId, 'from');
    
    let borderClass = 'border-l-blue-400';
    let bgClass = 'bg-white';
    let textColor = 'text-gray-800';
    let shadowColor = '0 2px 8px rgba(0, 0, 0, 0.06)';
    
    if (isCorrect) {
      borderClass = 'border-l-green-500';
      bgClass = 'bg-green-50';
      textColor = 'text-green-700';
      shadowColor = '0 4px 16px rgba(34, 197, 94, 0.2)';
    } else if (isSelected || isConnected) {
      borderClass = 'border-l-blue-500';
      bgClass = 'bg-blue-50';
      textColor = 'text-blue-700';
      shadowColor = '0 4px 16px rgba(59, 130, 246, 0.2)';
    }

    return (
      <div
        key={item.key}
        data-from={item.fromText}
        data-from-key={`${item.fromText}-${item.pairId}`}
        onClick={() => handleConnection({ fromText: item.fromText, pairId: item.pairId }, 'from')}
        className={`
          ${bgClass} p-4 rounded-lg cursor-pointer transition-all duration-300 group border-l-4 ${borderClass}
          ${!isCorrect ? 'hover:translate-x-1' : 'cursor-not-allowed'}
        `}
        style={{ 
          boxShadow: shadowColor,
        }}
        onMouseEnter={(e) => {
          if (!isCorrect) {
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(96, 165, 250, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isCorrect) {
            e.currentTarget.style.boxShadow = shadowColor;
          }
        }}
      >
        <div className="flex items-center gap-3">
          {item.fromImageUrl && (
            <div className="flex-shrink-0">
              <img 
                src={`${ASSETS_URL}${item.fromImageUrl}`} 
                alt="" 
                className="w-20 h-20 object-cover rounded"
              />
            </div>
          )}
          <div className="flex-1">
            <span className={`
              font-medium text-base transition-colors ${textColor}
            `}>
              {item.fromText}
            </span>
          </div>
        </div>
      </div>
    );
  })}
</div>

      {/* Right Column - TO items with word-list styling */}
      <div className="space-y-3">
        {shuffledToItems.map(item => {
          const isConnected = isItemConnected(item.toText, null, 'to');
          const isWrongTarget = wrongConnections.has(item.toText);
          const isHighlightedAsCorrect = highlightedCorrect.has(item.toText);
          
          let borderClass = 'border-l-blue-400';
          let bgClass = 'bg-white';
          let textColor = 'text-gray-800';
          let shadowColor = '0 2px 8px rgba(0, 0, 0, 0.06)';
          
          if (isCorrect) {
            borderClass = 'border-l-green-500';
            bgClass = 'bg-green-50';
            textColor = 'text-green-700';
            shadowColor = '0 4px 16px rgba(34, 197, 94, 0.2)';
          } else if (isWrongTarget) {
            borderClass = 'border-l-red-500';
            bgClass = 'bg-red-50';
            textColor = 'text-red-700';
            shadowColor = '0 4px 16px rgba(239, 68, 68, 0.2)';
          } else if (isHighlightedAsCorrect) {
            borderClass = 'border-l-green-500';
            bgClass = 'bg-green-50';
            textColor = 'text-green-700';
            shadowColor = '0 4px 16px rgba(34, 197, 94, 0.2)';
          } else if (isConnected) {
            borderClass = 'border-l-blue-500';
            bgClass = 'bg-blue-50';
            textColor = 'text-blue-700';
            shadowColor = '0 4px 16px rgba(59, 130, 246, 0.2)';
          }

          return (
            <div
              key={item.key}
              data-to={item.toText}
              onClick={() => handleConnection(item.toText, 'to')}
              className={`
                ${bgClass} p-4 rounded-lg cursor-pointer transition-all duration-300 group border-l-4 ${borderClass}
                ${!isCorrect && selectedFrom ? 'hover:translate-x-1' : ''}
                ${isCorrect ? 'cursor-not-allowed' : ''}
              `}
              style={{ 
                boxShadow: shadowColor,
              }}
              onMouseEnter={(e) => {
                if (!isCorrect && !isWrongTarget) {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(96, 165, 250, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCorrect) {
                  e.currentTarget.style.boxShadow = shadowColor;
                }
              }}
            >
              <div className="flex items-center gap-3">
                {item.toImageUrl && (
                  <div className="flex-shrink-0">
                    <img 
                      src={`${ASSETS_URL}${item.toImageUrl}`} 
                      alt="" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <span className={`
                    font-medium text-base transition-colors ${textColor}
                  `}>
                    {item.toText}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showSuccess && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-40">
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg shadow-lg">
            Аферым!✨
          </div>
        </div>
      )}
    </div>
  );
};

export default Connections;